import { z } from 'zod';

export const PayrollPermissionSchema = z.enum([
  'perm_payroll_view',
  'perm_payroll_admin',
  'perm_payroll_approve',
]);

export const PayPeriodStatusSchema = z.enum([
  'Draft',
  'In Progress',
  'Submitted for Approval',
  'Approved',
  'Rejected',
  'Exported',
  'Locked',
]);

export const EmploymentTypeSchema = z.enum([
  'Full-Time',
  'Part-Time',
  'Hourly',
  'Contractor',
]);

export const EmployeeTrackSchema = z.enum([
  'Judaic',
  'General',
  'Both',
  'N/A',
]);

export const EmployeeStatusSchema = z.enum([
  'Active',
  'On Leave',
  'Terminated',
]);

export const PositionTrackSchema = z.enum([
  'Judaic Studies',
  'General Studies',
  'Administrative',
  'Support',
  'N/A',
]);

export const CompensationTypeSchema = z.enum([
  'Annual Salary',
  'Monthly Salary',
  'Hourly Rate',
]);

export const PayDistributionSchema = z.enum([
  'Spread Evenly',
  'Custom Schedule',
]);

export const StipendScheduleSchema = z.enum([
  'Spread Evenly Across Pay Periods',
  'Lump Sum in Specific Period',
  'Custom Distribution',
]);

export const AdjustmentTypeSchema = z.enum([
  'Additional Pay',
  'Pay Reduction',
  'Correction',
  'Other',
]);

export const TaxTreatmentSchema = z.enum([
  'Taxable',
  'Tax-Exempt',
]);

export const DeductionTypeSchema = z.enum([
  'Health Insurance',
  'Dental',
  'Vision',
  '401k/403b',
  'FSA',
  'Childcare',
  'Union Dues',
  'Tzedakah',
  'Custom',
]);

export const DeductionTaxClassificationSchema = z.enum([
  'Pre-Tax',
  'Post-Tax',
]);

export const LeaveTypeSchema = z.enum([
  'Maternity',
  'Paternity',
  'Medical',
  'Personal',
  'Sabbatical',
]);

export const AuditActionSchema = z.enum([
  'employee_profile_updated',
  'position_upserted',
  'stipend_upserted',
  'time_entry_upserted',
  'adjustment_upserted',
  'external_tuition_payment_upserted',
  'deduction_entry_upserted',
  'period_submitted',
  'period_approved',
  'period_rejected',
  'period_unlocked',
  'period_exported',
]);

export const LeaveInfoSchema = z.object({
  leaveType: LeaveTypeSchema,
  startDate: z.string(),
  expectedReturnDate: z.string(),
  partialPayPercentage: z.number(),
});

export const PayPeriodSchema = z.object({
  id: z.string(),
  label: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: PayPeriodStatusSchema,
  exportDeadline: z.string(),
  totalGross: z.number(),
  employeesComplete: z.number().int(),
  totalEmployees: z.number().int(),
  hasHoliday: z.boolean(),
  holidayName: z.string().optional(),
  holidayDates: z.string().optional(),
  holidayRule: z.string().optional(),
  submittedAt: z.string().optional(),
  submittedBy: z.string().optional(),
  approvedAt: z.string().optional(),
  approvedBy: z.string().optional(),
  rejectionReason: z.string().optional(),
  exportCount: z.number().int().optional(),
});

export const PositionSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  title: z.string(),
  track: PositionTrackSchema,
  compensationType: CompensationTypeSchema,
  amount: z.number(),
  ftePercentage: z.number(),
  payDistribution: PayDistributionSchema,
  effectiveDate: z.string(),
  endDate: z.string().optional(),
  parsonageEnabled: z.boolean().optional(),
  parsonageAmount: z.number().optional(),
});

export const StipendAssignmentSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  type: z.string(),
  totalAmount: z.number(),
  paymentSchedule: StipendScheduleSchema,
  effectiveDate: z.string(),
  endDate: z.string(),
  scheduledPeriodId: z.string().optional(),
  distributionPeriodIds: z.array(z.string()).optional(),
  notes: z.string().optional(),
});

export const TimeEntrySchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  periodId: z.string(),
  date: z.string(),
  hours: z.number(),
  positionId: z.string(),
});

export const AdjustmentSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  periodId: z.string(),
  type: AdjustmentTypeSchema,
  amount: z.number(),
  description: z.string(),
  timestamp: z.string(),
});

export const ExternalTuitionPaymentSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  receivingSchoolName: z.string(),
  childName: z.string(),
  annualAmount: z.number(),
  taxTreatment: TaxTreatmentSchema,
  notes: z.string().optional(),
});

export const DeductionSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  deductionType: DeductionTypeSchema,
  customTypeName: z.string().optional(),
  taxClassification: DeductionTaxClassificationSchema,
  defaultAmount: z.number(),
  isRecurring: z.boolean(),
});

export const DeductionEntrySchema = z.object({
  id: z.string(),
  deductionId: z.string().optional(),
  employeeId: z.string(),
  periodId: z.string(),
  deductionType: DeductionTypeSchema,
  customTypeName: z.string().optional(),
  taxClassification: DeductionTaxClassificationSchema,
  amount: z.number(),
  isRecurring: z.boolean(),
});

export const EmployeePeriodCompSchema = z.object({
  periodId: z.string(),
  baseGross: z.number(),
  stipendGross: z.number(),
  adjustmentsGross: z.number(),
  externalTuitionGross: z.number(),
  deductionsTotal: z.number(),
  netPayEstimate: z.number(),
  completionState: z.enum(['complete', 'missing-data', 'needs-review']),
  pendingIssues: z.array(z.string()),
});

export const ProviderTemplateRefSchema = z.object({
  id: z.string(),
  provider: z.string(),
  templateName: z.string(),
  exportFormat: z.string(),
  lastUpdatedAt: z.string(),
});

export const ExportHistorySchema = z.object({
  id: z.string(),
  periodId: z.string(),
  providerTemplateId: z.string(),
  exportFormat: z.string(),
  exportedAt: z.string(),
  exportedBy: z.string(),
  rowCount: z.number().int(),
  incompleteRows: z.number().int(),
  downloadLabel: z.string(),
});

export const PayrollAuditLogSchema = z.object({
  id: z.string(),
  periodId: z.string().optional(),
  employeeId: z.string().optional(),
  action: AuditActionSchema,
  actorId: z.string(),
  actorLabel: z.string(),
  occurredAt: z.string(),
  summary: z.string(),
});

export const EmployeeSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().optional(),
  employmentType: EmploymentTypeSchema,
  track: EmployeeTrackSchema,
  status: EmployeeStatusSchema,
  startDate: z.string(),
  positions: z.array(PositionSchema),
  stipends: z.array(StipendAssignmentSchema),
  timeEntries: z.array(TimeEntrySchema),
  adjustments: z.array(AdjustmentSchema),
  externalTuitionPayments: z.array(ExternalTuitionPaymentSchema),
  deductions: z.array(DeductionSchema),
  deductionEntries: z.array(DeductionEntrySchema).default([]),
  leaveInfo: LeaveInfoSchema.optional(),
  annualCompTotal: z.number(),
  providerTemplateRefs: z.array(ProviderTemplateRefSchema).default([]),
});

export const PayrollDashboardEmployeeSchema = z.object({
  employeeId: z.string(),
  employeeRecordId: z.string(),
  fullName: z.string(),
  employmentType: EmploymentTypeSchema,
  track: EmployeeTrackSchema,
  status: EmployeeStatusSchema,
  annualCompTotal: z.number(),
  currentGross: z.number(),
  deductionsTotal: z.number(),
  netPayEstimate: z.number(),
  completionState: z.enum(['complete', 'missing-data', 'needs-review']),
  pendingIssues: z.array(z.string()),
});

export const PayrollPermissionsSchema = z.object({
  canView: z.boolean(),
  canAdmin: z.boolean(),
  canApprove: z.boolean(),
  canExport: z.boolean(),
});

export const PayrollDashboardSchema = z.object({
  selectedPeriodId: z.string(),
  periods: z.array(PayPeriodSchema),
  employees: z.array(PayrollDashboardEmployeeSchema),
  providerTemplateRefs: z.array(ProviderTemplateRefSchema).default([]),
  summary: z.object({
    selectedPeriodStatus: PayPeriodStatusSchema,
    totalEmployees: z.number().int(),
    employeesComplete: z.number().int(),
    employeesMissingData: z.number().int(),
    employeesNeedReview: z.number().int(),
    totalGross: z.number(),
    totalDeductions: z.number(),
    netPayEstimate: z.number(),
  }),
  permissions: PayrollPermissionsSchema,
});

export const PayrollEmployeeDetailSchema = z.object({
  employee: EmployeeSchema,
  selectedPeriod: PayPeriodSchema,
  periodComp: EmployeePeriodCompSchema,
  auditLog: z.array(PayrollAuditLogSchema),
  permissions: PayrollPermissionsSchema,
});

export const PayrollAuditLogConnectionSchema = z.object({
  items: z.array(PayrollAuditLogSchema),
  totalCount: z.number().int(),
  nextToken: z.string().nullable().optional(),
});

export const ExportPayrollPeriodResultSchema = z.object({
  dashboard: PayrollDashboardSchema,
  exportHistory: ExportHistorySchema,
});

const TenantScopedSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
}).strict();

export const GetPayrollDashboardInputSchema = TenantScopedSchema;

export const GetPayrollEmployeeDetailInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
}).strict();

export const SwitchPayrollPeriodInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string(),
}).strict();

export const UpsertPayrollEmployeeProfileInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
  profile: z.object({
    email: z.string().optional(),
    status: EmployeeStatusSchema.optional(),
    leaveInfo: LeaveInfoSchema.nullish(),
  }).strict(),
}).strict();

export const UpsertPayrollPositionInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
  position: PositionSchema.partial().extend({
    id: z.string().optional(),
  }),
}).strict();

export const UpsertPayrollStipendInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
  stipend: StipendAssignmentSchema.partial().extend({
    id: z.string().optional(),
  }),
}).strict();

export const UpsertPayrollTimeEntryInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
  timeEntry: TimeEntrySchema.partial().extend({
    id: z.string().optional(),
  }),
}).strict();

export const UpsertPayrollAdjustmentInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
  adjustment: AdjustmentSchema.partial().extend({
    id: z.string().optional(),
  }),
}).strict();

export const UpsertPayrollExternalTuitionPaymentInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
  externalTuitionPayment: ExternalTuitionPaymentSchema.partial().extend({
    id: z.string().optional(),
  }),
}).strict();

export const UpsertPayrollDeductionEntryInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string().optional(),
  employeeId: z.string(),
  deductionEntry: DeductionEntrySchema.partial().extend({
    id: z.string().optional(),
  }),
}).strict();

export const SubmitPayrollPeriodForApprovalInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string(),
  notes: z.string().optional(),
}).strict();

export const ApprovePayrollPeriodInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string(),
  notes: z.string().optional(),
}).strict();

export const RejectPayrollPeriodInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string(),
  reason: z.string().min(1),
}).strict();

export const UnlockPayrollPeriodInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string(),
  reason: z.string().optional(),
}).strict();

export const ExportPayrollPeriodInputSchema = z.object({
  tenantId: z.string().optional(),
  periodId: z.string(),
  providerTemplateId: z.string(),
  exportFormat: z.string().default('csv'),
}).strict();

export const ListPayrollAuditLogInputSchema = z.object({
  tenantId: z.string().optional(),
  employeeId: z.string().optional(),
  periodId: z.string().optional(),
  limit: z.number().int().positive().max(100).optional(),
}).strict();

export type PayrollPermission = z.infer<typeof PayrollPermissionSchema>;
export type PayPeriodStatus = z.infer<typeof PayPeriodStatusSchema>;
export type EmploymentType = z.infer<typeof EmploymentTypeSchema>;
export type EmployeeTrack = z.infer<typeof EmployeeTrackSchema>;
export type EmployeeStatus = z.infer<typeof EmployeeStatusSchema>;
export type PositionTrack = z.infer<typeof PositionTrackSchema>;
export type CompensationType = z.infer<typeof CompensationTypeSchema>;
export type PayDistribution = z.infer<typeof PayDistributionSchema>;
export type StipendSchedule = z.infer<typeof StipendScheduleSchema>;
export type AdjustmentType = z.infer<typeof AdjustmentTypeSchema>;
export type TaxTreatment = z.infer<typeof TaxTreatmentSchema>;
export type DeductionType = z.infer<typeof DeductionTypeSchema>;
export type DeductionTaxClassification = z.infer<typeof DeductionTaxClassificationSchema>;
export type LeaveType = z.infer<typeof LeaveTypeSchema>;
export type AuditAction = z.infer<typeof AuditActionSchema>;
export type LeaveInfo = z.infer<typeof LeaveInfoSchema>;
export type PayPeriod = z.infer<typeof PayPeriodSchema>;
export type Position = z.infer<typeof PositionSchema>;
export type StipendAssignment = z.infer<typeof StipendAssignmentSchema>;
export type TimeEntry = z.infer<typeof TimeEntrySchema>;
export type Adjustment = z.infer<typeof AdjustmentSchema>;
export type ExternalTuitionPayment = z.infer<typeof ExternalTuitionPaymentSchema>;
export type Deduction = z.infer<typeof DeductionSchema>;
export type DeductionEntry = z.infer<typeof DeductionEntrySchema>;
export type EmployeePeriodComp = z.infer<typeof EmployeePeriodCompSchema>;
export type ProviderTemplateRef = z.infer<typeof ProviderTemplateRefSchema>;
export type ExportHistory = z.infer<typeof ExportHistorySchema>;
export type PayrollAuditLog = z.infer<typeof PayrollAuditLogSchema>;
export type Employee = z.infer<typeof EmployeeSchema>;
export type PayrollDashboardEmployee = z.infer<typeof PayrollDashboardEmployeeSchema>;
export type PayrollPermissions = z.infer<typeof PayrollPermissionsSchema>;
export type PayrollDashboard = z.infer<typeof PayrollDashboardSchema>;
export type PayrollEmployeeDetail = z.infer<typeof PayrollEmployeeDetailSchema>;
export type PayrollAuditLogConnection = z.infer<typeof PayrollAuditLogConnectionSchema>;
export type ExportPayrollPeriodResult = z.infer<typeof ExportPayrollPeriodResultSchema>;
